import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { isArray } from 'lodash';
import React from 'react';
import { ExposureConfigurationResponse } from '../../../api/ConfigApi';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { Peril } from '../../../types/policy';
import LimitsForm from '../Perils/LimitsForm';
import { filterForPolicySnapshot } from '../util_sharedEndorsementAndRenewal';
import ReviewChangesField from './ReviewChangesField';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	perils: Peril[];
	characteristicTimestamp: string;
	referenceTimestamp: string;
	removedPerils: string[];
	exposureConfiguration: ExposureConfigurationResponse;
}

const RemovedPerils = (props: Props) => {
	const {
		perils,
		characteristicTimestamp,
		referenceTimestamp,
		removedPerils,
		exposureConfiguration,
		policyData,
		authData,
	} = props;
	const { policy } = policyData;
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	if (!removedPerils.length) {
		return null;
	}

	const renderFields = (peril: Peril) => {
		const perilConfig = exposureConfiguration.perils.find(
			(config) => config.name === peril.name,
		);
		const perilCharacteristic = peril.characteristics
			.filter((c) =>
				filterForPolicySnapshot(
					c.createdTimestamp,
					c.replacedTimestamp,
					referenceTimestamp,
				),
			)
			.find(
				(char) =>
					parseInt(char.coverageStartTimestamp, 10) <=
						parseInt(characteristicTimestamp, 10) &&
					parseInt(char.coverageEndTimestamp, 10) >=
						parseInt(characteristicTimestamp, 10),
			);
		const fieldConfigs = perilConfig.fields;
		return fieldConfigs.map((field, ind) => {
			const value = perilCharacteristic.fieldValues[field.name];
			const displayVal = isArray(value) ? value[0] : value;
			return (
				<Grid item key={`${field}-${ind}`}>
					<ReviewChangesField
						field={field}
						value={displayVal}
						fieldGroupsByLocator={null}
						allValues={perilCharacteristic.fieldValues}
						currentFieldValues={perilCharacteristic.fieldValues}
						addFieldGroups={perilCharacteristic.addFieldGroups}
					/>
				</Grid>
			);
		});
	};

	const renderLimits = (peril: Peril) => {
		const perilCharacteristic = peril.characteristics
			.filter((c) =>
				filterForPolicySnapshot(
					c.createdTimestamp,
					c.replacedTimestamp,
					referenceTimestamp,
				),
			)
			.find(
				(char) =>
					parseInt(char.coverageStartTimestamp, 10) <=
						parseInt(characteristicTimestamp, 10) &&
					parseInt(char.coverageEndTimestamp, 10) >=
						parseInt(characteristicTimestamp, 10),
			);
		return (
			<LimitsForm
				perilObj={perilCharacteristic}
				id={`${peril.locator}-${Math.random()}`}
				perilConfig={perilCharacteristic}
				currency={policy?.currency}
				isInEditMode={false}
				userHasWriteAccess={false}
				updateLimit={null}
			/>
		);
	};

	const renderRemovedPerilAccordions = () => {
		return removedPerils.map((locator, ind) => {
			const peril = perils.find((p) => p.locator === locator);
			const perilConfig = exposureConfiguration.perils.find(
				(config) => config.name === peril.name,
			);
			const displayName = perilConfig.displayName;
			return (
				<Accordion key={`removedPeril-${ind}`}>
					<AccordionSummary
						style={{ borderBottom: '1px solid rgba(0, 0, 0, .125)' }}
						expandIcon={<ExpandMoreIcon />}
					>{`Removed: ${displayName}`}</AccordionSummary>
					<AccordionDetails
						style={{ display: 'flex', flexDirection: 'column' }}
					>
						<Grid container spacing={1}>
							{renderFields(peril)}
						</Grid>
						{!hidePerilLimitsEnabled && renderLimits(peril)}
					</AccordionDetails>
				</Accordion>
			);
		});
	};

	return <>{renderRemovedPerilAccordions()}</>;
};

export default withPolicyContext<Props>(RemovedPerils);
